import { canUsePrimalStrength } from '../../../shared/rules/primalKnowledge.mjs';
import { barbarianSubclass } from '../../../shared/rules/berserkerFrenzy.mjs';
import { recklessWindow, resolveRecklessActorId } from '../../../shared/rules/recklessAttack.mjs';
import { savingThrowRollProfile } from '../../../shared/rules/savingThrowRollMode.mjs';
import { getRageLevel, hasHeavyArmor, hasPersistentRage, isRageEffective } from '../../../shared/rules/barbarianRage.mjs';
import { withoutSuppressedAdvantages } from './advantageSuppression';
import {championInitiativeSources} from '../../../shared/rules/champion.mjs';
import { applyActiveEffect, hasActiveEffect, removeActiveEffect } from './combatTimeline';

export const BARBARIAN_PROGRESSION = [
  { level: 1, rageUses: 2, rageDamage: 2, weaponMasteryCount: 2 },
  { level: 2, rageUses: 2, rageDamage: 2, weaponMasteryCount: 2 },
  { level: 3, rageUses: 3, rageDamage: 2, weaponMasteryCount: 2 },
  { level: 4, rageUses: 3, rageDamage: 2, weaponMasteryCount: 3 },
  { level: 5, rageUses: 3, rageDamage: 2, weaponMasteryCount: 3 },
  { level: 6, rageUses: 4, rageDamage: 2, weaponMasteryCount: 3 },
  { level: 7, rageUses: 4, rageDamage: 2, weaponMasteryCount: 3 },
  { level: 8, rageUses: 4, rageDamage: 2, weaponMasteryCount: 3 },
  { level: 9, rageUses: 4, rageDamage: 3, weaponMasteryCount: 3 },
  { level: 10, rageUses: 4, rageDamage: 3, weaponMasteryCount: 4 },
  { level: 11, rageUses: 4, rageDamage: 3, weaponMasteryCount: 4 },
  { level: 12, rageUses: 5, rageDamage: 3, weaponMasteryCount: 4 },
  { level: 13, rageUses: 5, rageDamage: 3, weaponMasteryCount: 4 },
  { level: 14, rageUses: 5, rageDamage: 3, weaponMasteryCount: 4 },
  { level: 15, rageUses: 5, rageDamage: 3, weaponMasteryCount: 4 },
  { level: 16, rageUses: 5, rageDamage: 4, weaponMasteryCount: 4 },
  { level: 17, rageUses: 6, rageDamage: 4, weaponMasteryCount: 4 },
  { level: 18, rageUses: 6, rageDamage: 4, weaponMasteryCount: 4 },
  { level: 19, rageUses: 6, rageDamage: 4, weaponMasteryCount: 4 },
  { level: 20, rageUses: 6, rageDamage: 4, weaponMasteryCount: 4 },
];

const resolveD20RollMode = ({ advantageSources = [], disadvantageSources = [] } = {}) => {
  const hasAdvantage = Array.isArray(advantageSources) && advantageSources.length > 0;
  const hasDisadvantage = Array.isArray(disadvantageSources) && disadvantageSources.length > 0;
  if (hasAdvantage && !hasDisadvantage) return "advantage";
  if (hasDisadvantage && !hasAdvantage) return "disadvantage";
  return "normal";
};

const normalize = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase();

export const getBarbarianLevel = (character) => {
  if (!Array.isArray(character?.occupation)) return 0;
  return character.occupation.reduce((total, entry) => {
    const name = normalize(
      entry?.Name ?? entry?.Occupation ?? entry?.name ?? entry?.occupation
    );
    if (name !== "barbarian") return total;
    const level = Number(entry?.Level ?? entry?.level ?? 0);
    return total + (Number.isFinite(level) && level > 0 ? level : 0);
  }, 0);
};

export const getBarbarianProgression = (levelOrCharacter) => {
  const level =
    typeof levelOrCharacter === "number"
      ? levelOrCharacter
      : getBarbarianLevel(levelOrCharacter);
  return BARBARIAN_PROGRESSION.reduce(
    (best, row) => (level >= row.level ? row : best),
    null
  );
};

export const hasHeavyArmorEquipped = hasHeavyArmor;

export const isRageActive = (character) =>
  Boolean(
    character?.classState?.barbarian?.rage?.active ??
      character?.barbarian?.rage?.active
  );
export const getRageUsesRemaining = (character) => {
  const max = getBarbarianProgression(character)?.rageUses || 0;
  const raw =
    character?.classState?.barbarian?.rage?.current ??
    character?.barbarian?.rage?.current;
  const current = Number(raw);
  return Number.isFinite(current)
    ? Math.max(0, Math.min(max, Math.floor(current)))
    : max;
};

/**
 * What the Active Conditions window says about an active Rage (2024): its benefits, how to keep it going, and when it
 * ends, from the server's Rage state and whose turn it is.
 */
export const describeRageCondition = (character, combatState = {}, combatantId) => {
  const raw = character?.classState?.barbarian?.rage || {};
  const bonus = getBarbarianProgression(getRageLevel(character))?.rageDamage || 0;
  const participant = combatState?.participants?.[combatState?.activeTurn];
  const ownTurn = Boolean(participant) && String(participant.characterId || participant.enemyId) === String(combatantId);
  const limit = Number.isFinite(raw.secondsRemaining) ? `, at most ${Math.max(1, Math.ceil(raw.secondsRemaining / 60))} more min` : '';
  if (hasPersistentRage(character)) {
    return {
      description: `Resistance to Bludgeoning, Piercing and Slashing damage. +${bonus} damage on attacks using Strength (weapon or Unarmed Strike). `
        + "Advantage on Strength checks and Strength saving throws. You can't cast spells or keep Concentration. "
        + 'Persistent Rage: it lasts its full 10 minutes without being kept going, ending early only if you fall Unconscious or put on Heavy armor.',
      duration: Number.isFinite(raw.secondsRemaining) ? `${Math.max(1, Math.ceil(raw.secondsRemaining / 60))} more min` : '10 minutes',
    };
  }
  const duration = !participant ? `Until the end of your first turn in combat unless you keep it going${limit}`
    : raw.ownTurnsRemaining >= 2 ? `Kept going: until the end of your next turn${limit}`
    : ownTurn ? `Ends at the end of this turn unless you keep it going${limit}`
    : `Until the end of your next turn${limit}`;
  return {
    description: `Resistance to Bludgeoning, Piercing and Slashing damage. +${bonus} damage on attacks using Strength (weapon or Unarmed Strike). `
      + "Advantage on Strength checks and Strength saving throws. You can't cast spells or keep Concentration. "
      + 'Keep it going each turn by attacking an enemy, forcing an enemy to make a saving throw, or taking a Bonus Action. '
      + 'It also ends when combat ends, if you put on Heavy armor, or if you are Incapacitated.',
    duration,
  };
};

export const getRageState = (character) => {
  const progression = getBarbarianProgression(character);
  const max = progression?.rageUses || 0;
  return {
    active: isRageActive(character),
    current: getRageUsesRemaining(character),
    max,
    rageDamage: progression?.rageDamage || 0,
  };
};

const withRage = (character, rage) => ({
  ...character,
  classState: {
    ...(character?.classState || {}),
    barbarian: { ...(character?.classState?.barbarian || {}), rage },
  },
});

export const activateRage = (character) => {
  const state = getRageState(character);
  if (state.active || state.current <= 0 || hasHeavyArmorEquipped(character))
    return character;
  return withRage(character, { active: true, current: state.current - 1 });
};

export const endRage = (character) => {
  const state = getRageState(character);
  if (!state.active) return character;
  return withRage(character, { active: false, current: state.current });
};

export const applyRageRest = (character, restType) => {
  const state = getRageState(character);
  if (!state.max) return character;
  const long = restType === "long";
  const next = withRage(character, {
    active: long ? false : state.active,
    current: long ? state.max : Math.min(state.max, state.current + 1),
  });
  return next;
};

const hasCondition = (character, conditionName) => {
  const target = normalize(conditionName);
  const conditions = [
    ...(Array.isArray(character?.conditions) ? character.conditions : []),
    ...(Array.isArray(character?.statusConditions) ? character.statusConditions : []),
  ];
  return conditions.some((condition) =>
    normalize(condition?.name ?? condition?.label ?? condition?.id ?? condition) === target
  );
};

export const BARBARIAN_SUBCLASSES = [
  {
    id: "path-of-the-berserker",
    name: "Path of the Berserker",
    classId: "barbarian",
    level: 3,
    features: [
      {
        id: "berserker-frenzy",
        name: "Frenzy",
        level: 3,
        description:
          "If you use Reckless Attack while your Rage is active, you deal extra damage to the first target you hit on your turn with a Strength-based attack. To determine the extra damage, roll a number of d6s equal to your Rage Damage bonus, and add them together. The damage has the same type as the weapon or Unarmed Strike used for the attack.",
      },
      {
        id: "berserker-mindless-rage",
        name: "Mindless Rage",
        level: 6,
        description:
          "You have Immunity to the Charmed and Frightened conditions while your Rage is active. If you’re Charmed or Frightened when you enter your Rage, the condition ends on you.",
      },
      { id: "berserker-retaliation", name: "Retaliation", level: 10, type: "reaction", hideUseButton: true,
        description: "After you take damage from a creature within 5 feet, you can use your Reaction to make one melee attack against it." },
      { id: "berserker-intimidating-presence", name: "Intimidating Presence", level: 14,
        description: "As a Bonus Action, each creature of your choice in a 30-foot Emanation from you makes a Wisdom saving throw (DC 8 + your Strength modifier + your Proficiency Bonus). On a failure it is Frightened for 1 minute, repeating the save at the end of each of its turns and ending the effect on a success. Once used, it returns when you finish a Long Rest, or when you expend a use of your Rage (no action required) to restore it." },
    ],
  },
];

export const BARBARIAN_LEVEL_1_SKILLS = [
  "animalHandling",
  "athletics",
  "intimidation",
  "nature",
  "perception",
  "survival",
];

export const PRIMAL_KNOWLEDGE_SKILLS = [
  "acrobatics",
  "intimidation",
  "perception",
  "stealth",
  "survival",
];

export const getBarbarianSubclass = barbarianSubclass;

export const getAvailableBarbarianSubclasses = (character) =>
  getBarbarianLevel(character) >= 3 ? BARBARIAN_SUBCLASSES : [];

export const isPathOfTheBerserker = (character) => {
  const subclass = getBarbarianSubclass(character);
  const id = normalize(subclass?.id ?? subclass?.key ?? subclass?.name ?? subclass);
  return id === "path-of-the-berserker" || id === "path of the berserker" || id === "berserker";
};

export const getActiveBarbarianSubclassFeatures = (character) => {
  const level = getBarbarianLevel(character);
  const selected = getBarbarianSubclass(character);
  if (!selected || level < 3) return [];
  const selectedId = normalize(selected?.id ?? selected?.key ?? selected?.name ?? selected);
  const subclass = BARBARIAN_SUBCLASSES.find((entry) => {
    const entryId = normalize(entry.id);
    const entryName = normalize(entry.name);
    return selectedId === entryId || selectedId === entryName || selectedId === "berserker";
  });
  return (subclass?.features || []).filter((feature) => level >= feature.level);
};

export const hasPrimalKnowledge = (character) => getBarbarianLevel(character) >= 3;
export const hasFeralInstinct = (character) => getBarbarianLevel(character) >= 7;

export const getFeralInstinctInitiativeSources = (character) =>
  hasFeralInstinct(character) ? ["Feral Instinct"] : [];

export const resolveInitiativeRollMode = (character, options = {}) => {
  const advantageSources = Array.isArray(options.advantageSources)
    ? [...options.advantageSources]
    : [];
  const disadvantageSources = Array.isArray(options.disadvantageSources)
    ? [...options.disadvantageSources]
    : [];

  advantageSources.push(...getFeralInstinctInitiativeSources(character), ...championInitiativeSources(character));

  return {
    mode: resolveD20RollMode({ advantageSources, disadvantageSources }),
    advantageSources,
    disadvantageSources,
  };
};
export const isPrimalKnowledgeSkill = (skillKey) =>
  PRIMAL_KNOWLEDGE_SKILLS.includes(normalize(skillKey));
export const canUsePrimalKnowledgeForSkill = canUsePrimalStrength;

export const BARBARIAN_FEATURES = [
  {
    id: "barbarian-rage",
    name: "Rage",
    class: "Barbarian",
    classId: "barbarian",
    level: 1,
    type: "toggle",
  },
  {
    id: "barbarian-weapon-mastery",
    name: "Weapon Mastery",
    class: "Barbarian",
    classId: "barbarian",
    level: 1,
    type: "configuration",
    description:
      "Your training with weapons allows you to use the mastery property of selected simple or martial melee weapon types. You can replace one selection when you finish a Long Rest.",
  },
  {
    id: "danger-sense",
    name: "Danger Sense",
    class: "Barbarian",
    classId: "barbarian",
    level: 2,
    type: "passive",
    description:
      "You gain an uncanny sense of when things aren’t as they should be, giving you an edge when you dodge perils.\n\nYou have Advantage on Dexterity saving throws unless you have the Incapacitated condition.",
    hideUseButton: true,
  },
  {
    id: "barbarian-primal-knowledge",
    name: "Primal Knowledge",
    class: "Barbarian",
    classId: "barbarian",
    level: 3,
    type: "configuration",
    description:
      "You gain proficiency in one additional Barbarian skill. While raging, you can use Strength for Acrobatics, Intimidation, Perception, Stealth, and Survival checks.",
  },
  {
    id: "barbarian-subclass",
    name: "Barbarian Subclass",
    class: "Barbarian",
    classId: "barbarian",
    level: 3,
    type: "configuration",
    description: "Choose a Barbarian subclass. Path of the Berserker is currently available.",
  },
  {
    id: "feral-instinct",
    name: "Feral Instinct",
    class: "Barbarian",
    classId: "barbarian",
    level: 7,
    type: "passive",
    description:
      "Your instincts are so honed that you have Advantage on Initiative rolls.",
  },
  {
    id: "instinctive-pounce",
    name: "Instinctive Pounce",
    class: "Barbarian",
    classId: "barbarian",
    level: 7,
    type: "passive",
    description:
      "As part of the Bonus Action you take to enter your Rage, you can move up to half your Speed.",
  },
  {
    id: "reckless-attack",
    name: "Reckless Attack",
    class: "Barbarian",
    classId: "barbarian",
    level: 2,
    type: "toggle",
    description:
      "You can throw aside all concern for defense to attack with increased ferocity. When you make your first attack roll on your turn, you can decide to attack recklessly. Doing so gives you Advantage on attack rolls using Strength until the start of your next turn, but attack rolls against you have Advantage during that time.\n\nReady this feature before your first attack roll. Both benefits and the defensive drawback last until the start of your next turn.",
  },
  {
    id: "brutal-strike",
    name: "Brutal Strike",
    class: "Barbarian",
    classId: "barbarian",
    level: 9,
    type: "toggle",
    icon: "fist",
    description:
      "If you use Reckless Attack, you can forgo any Advantage on one Strength-based attack roll of your choice on your turn (the roll can’t have Disadvantage). If it hits, the target takes an extra 1d10 damage of the attack’s type, and you choose one Brutal Strike effect: Forceful Blow (pushed 15 feet straight away from you; you can then move up to half your Speed straight toward it without provoking Opportunity Attacks) or Hamstring Blow (its Speed drops by 15 feet until the start of your next turn). Once per turn.",
  },
];

export const getAvailableBarbarianFeatures = (character) => {
  const barbarianLevel = getBarbarianLevel(character);
  const selectedSubclass = getBarbarianSubclass(character);
  const selectedSubclassName =
    typeof selectedSubclass === "object" && selectedSubclass !== null
      ? selectedSubclass.name || selectedSubclass.label || selectedSubclass.id
      : selectedSubclass;
  return [
    ...BARBARIAN_FEATURES.filter((feature) => barbarianLevel >= feature.level).map((feature) =>
      feature.id === "barbarian-subclass" && selectedSubclassName
        ? { ...feature, subclass: selectedSubclassName }
        : feature
    ),
    ...getActiveBarbarianSubclassFeatures(character).map((feature) => ({
      ...feature,
      class: "Barbarian",
      classId: "barbarian",
      subclass: "Path of the Berserker",
      type: "passive",
    })),
  ];
};

export const hasDangerSense = (character) => getBarbarianLevel(character) >= 2;

export const getDangerSenseSavingThrowSources = (character, abilityKey, options = {}) =>
  savingThrowRollProfile(character, abilityKey, options).advantageSources.filter(source => source === 'Danger Sense');

export const resolveSavingThrowRollMode = (character, abilityKey, options = {}) =>
  savingThrowRollProfile(character, abilityKey, options);

export const getRageBenefits = (character) =>
  isRageActive(character)
    ? {
        resistances: ["bludgeoning", "piercing", "slashing"],
        advantage: { abilityChecks: ["str"], savingThrows: ["str"] },
        blocksSpellcasting: true,
        blocksConcentration: true,
      }
    : {
        resistances: [],
        advantage: { abilityChecks: [], savingThrows: [] },
        blocksSpellcasting: false,
        blocksConcentration: false,
      };

// Rage Damage applies only while Rage is in effect (not ended by Heavy armor, Incapacitation or its deadline), to a
// Strength-based weapon or Unarmed Strike attack.
export const getRageDamageBonus = (character, attack = {}, combatState = {}) => {
  if (!isRageEffective(character, combatState)) return 0;
  const ability = normalize(attack.ability ?? attack.abilityKey ?? attack.stat);
  const kind = normalize(attack.kind ?? attack.type ?? attack.attackType);
  const isQualifyingKind =
    kind.includes("weapon") ||
    kind.includes("unarmed") ||
    attack.isWeaponAttack ||
    attack.isUnarmedStrike;
  if (
    ability !== "str" ||
    attack.isSpellAttack ||
    !isQualifyingKind ||
    attack.dealsDamage === false
  )
    return 0;
  return getBarbarianProgression(getRageLevel(character))?.rageDamage || 0;
};

export const isEligibleBarbarianWeaponMastery = (weapon) => {
  const category = normalize(weapon?.category ?? weapon?.weaponCategory);
  const name = normalize(weapon?.name ?? weapon?.type);
  return (
    Boolean(name) &&
    (category === "simple melee" ||
      category === "martial melee" ||
      category.includes("simple melee") ||
      category.includes("martial melee"))
  );
};

export const validateBarbarianWeaponMasteries = (
  character,
  selections = []
) => {
  const count = getBarbarianProgression(character)?.weaponMasteryCount || 0;
  const seen = new Set();
  const valid = [];
  for (const weapon of selections) {
    const key = normalize(weapon?.type ?? weapon?.name ?? weapon);
    if (!key || seen.has(key)) continue;
    if (typeof weapon === "object" && !isEligibleBarbarianWeaponMastery(weapon))
      continue;
    seen.add(key);
    valid.push(key);
  }
  return {
    valid: valid.slice(0, count),
    count,
    hasDuplicates: seen.size < selections.length,
  };
};

export const getWeaponMasteryState = (character) => {
  const progression = getBarbarianProgression(character);
  const count = progression?.weaponMasteryCount || 0;
  const selections =
    character?.classState?.barbarian?.weaponMasteries?.selections || [];
  return {
    selections: validateBarbarianWeaponMasteries(character, selections).valid,
    count,
    // Replacement authority now lives in weaponMasteries.sources.barbarian.
    // The deprecated class-local flag is intentionally ignored.
    canReplaceAfterLongRest: false,
  };
};

const withWeaponMasteries = (character, weaponMasteries) => ({
  ...character,
  classState: {
    ...(character?.classState || {}),
    barbarian: { ...(character?.classState?.barbarian || {}), weaponMasteries },
  },
});

export const setWeaponMasterySelections = (character, selections = []) => {
  const result = validateBarbarianWeaponMasteries(character, selections);
  if (result.valid.length !== selections.length || result.hasDuplicates) {
    return character;
  }
  return withWeaponMasteries(character, {
    ...(character?.classState?.barbarian?.weaponMasteries || {}),
    selections: result.valid,
  });
};

export const replaceWeaponMasteryAfterLongRest = (
  character,
  fromKey,
  replacementWeapon
) => {
  const state = getWeaponMasteryState(character);
  if (!state.canReplaceAfterLongRest) return character;
  const normalizedFrom = normalize(fromKey);
  const normalizedReplacement = normalize(
    replacementWeapon?.type ?? replacementWeapon?.name ?? replacementWeapon
  );
  if (!normalizedFrom || !state.selections.includes(normalizedFrom))
    return character;
  if (
    typeof replacementWeapon === "object" &&
    !isEligibleBarbarianWeaponMastery(replacementWeapon)
  )
    return character;
  if (state.selections.includes(normalizedReplacement)) return character;
  const selections = state.selections.map((entry) =>
    entry === normalizedFrom ? normalizedReplacement : entry
  );
  return withWeaponMasteries(character, {
    selections,
    canReplaceAfterLongRest: false,
  });
};


const withRecklessAttack = (character, recklessAttack) => ({
  ...character,
  classState: {
    ...(character?.classState || {}),
    barbarian: { ...(character?.classState?.barbarian || {}), recklessAttack },
  },
});

export const getRecklessAttackState = (character) => {
  const raw = character?.classState?.barbarian?.recklessAttack || {};
  return {
    active: getBarbarianLevel(character) >= 2 && Boolean(raw.active),
    firstAttackMade: Boolean(raw.firstAttackMade),
    declared: Boolean(raw.declared ?? raw.active),
    defensiveDrawbackPending: Boolean(raw.defensiveDrawbackPending),
  };
};

export const canActivateRecklessAttack = (character) => ({
  allowed: getBarbarianLevel(character) >= 2,
  reason: getBarbarianLevel(character) < 2 ? "Reckless Attack requires Barbarian level 2." : null,
});

export const declareRecklessAttack = (character) => {
  if (!canActivateRecklessAttack(character).allowed) return character;
  const state = getRecklessAttackState(character);
  if (state.active) return character;
  return withRecklessAttack(character, {
    active: true,
    declared: true,
    firstAttackMade: false,
    defensiveDrawbackPending: true,
  });
};

export const createRecklessAttackEffect = (combatantId) => ({
  id: `reckless-attack:${combatantId}`,
  definitionId: 'reckless-attack',
  name: 'Reckless Attack',
  sourceCombatantId: combatantId,
  targetCombatantId: combatantId,
  expiration: { type: 'sourceTurn', combatantId, boundary: 'start', remainingOccurrences: 1 },
  stackKey: `reckless-attack:${combatantId}`,
  stackPolicy: 'ignore',
  description: 'Reckless Attack active. Attack rolls against this character have Advantage until the start of their next turn.',
});

export const activateRecklessAttack = (character, combatState, combatantId) => {
  const eligibility = canActivateRecklessAttack(character);
  if (!eligibility.allowed) throw new Error(eligibility.reason);
  if (!combatantId) throw new Error('Reckless Attack requires an active combatant.');
  return {
    character: declareRecklessAttack(character),
    combatState: applyActiveEffect(combatState, createRecklessAttackEffect(combatantId)),
  };
};

export const endRecklessAttack = (character) => {
  const state = getRecklessAttackState(character);
  if (!state.active && !state.firstAttackMade && !state.declared) return character;
  return resetFrenzyTurn(withRecklessAttack(character, { active: false, declared: false, firstAttackMade: false }));
};

export const markBarbarianAttackRoll = (character) => character;

/**
 * Reckless Attack is evaluated from the resolved attack, after any choice of
 * attack ability has been made.  Callers should provide isMeleeAttack on new
 * attack models; the kind fallback keeps older attack producers compatible.
 */
export const qualifiesForRecklessAttack = (attack = {}) => {
  const ability = normalize(
    attack.attackAbility ?? attack.ability ?? attack.abilityKey ?? attack.stat
  );
  return (ability === "str" || ability === "strength") && !attack.isDamageRoll;
};

export const getRecklessAttackAdvantageSources = (character, attack = {}, options = {}) => {
  const state = options.combatState
    ? recklessWindow(options.combatState, resolveRecklessActorId(character, options.combatState, options.actorId ?? character?._id ?? character?.characterId))
    : getRecklessAttackState(character);
  const enabled = state.active || (state.ready && !state.firstAttackMade);
  return enabled && qualifiesForRecklessAttack(attack) ? ["Reckless Attack"] : [];
};

export const resolveAttackRollMode = (character, attack = {}, options = {}) => {
  const advantageSources = Array.isArray(options.advantageSources)
    ? [...options.advantageSources]
    : [];
  const disadvantageSources = Array.isArray(options.disadvantageSources)
    ? [...options.disadvantageSources]
    : [];
  advantageSources.push(...getRecklessAttackAdvantageSources(character, attack, options));
  const resolvedAdvantages = withoutSuppressedAdvantages(advantageSources, options.suppressedAdvantageSources || []);
  return {
    mode: resolveD20RollMode({ advantageSources: resolvedAdvantages, disadvantageSources }),
    advantageSources: resolvedAdvantages,
    disadvantageSources,
  };
};

// Before the turn's first attack roll Brutal Strike can be readied; that roll is then made recklessly.
export const BRUTAL_STRIKE_ERROR = "Brutal Strike requires Reckless Attack to be active.";

export const getBrutalStrikePendingEffect = (combatState, combatantId) =>
  (combatState?.activeEffects || []).find((effect) =>
    effect.definitionId === 'brutal-strike-pending' && effect.sourceCombatantId === combatantId);

export const isBrutalStrikeEligibleAttack = ({ attack = {}, ability, rollMode } = {}) => {
  const actualAbility = normalize(ability ?? attack.attackAbility ?? attack.ability ?? attack.abilityKey ?? attack.stat);
  const kind = normalize(attack.kind ?? attack.type ?? attack.attackType);
  const qualifyingKind = kind.includes('weapon') || kind.includes('unarmed') || attack.isWeaponAttack || attack.isUnarmedStrike;
  return (actualAbility === 'str' || actualAbility === 'strength') && qualifyingKind && !attack.isSpellAttack && rollMode !== 'disadvantage';
};

export const canActivateBrutalStrike = ({ character, combatState, combatant, combatantId, currentTurnCombatantId } = {}) => {
  const barbarianCombatantId = combatantId ?? combatant?.characterId ?? combatant?.id;
  const activeCombatantId = currentTurnCombatantId ?? combatState?.participants?.[combatState?.activeTurn]?.characterId;
  let reason = null;
  if (getBarbarianLevel(character) < 9) reason = 'Brutal Strike requires Barbarian level 9.';
  else if (!barbarianCombatantId || barbarianCombatantId !== activeCombatantId) reason = "Brutal Strike can be activated only on the Barbarian's turn.";
  // Reckless Attack active, or readied for this turn's first attack roll (which Brutal Strike may then be).
  else if (!hasActiveEffect(combatState, barbarianCombatantId, 'reckless-attack') && !recklessWindow(combatState, barbarianCombatantId).ready
    && recklessWindow(combatState, barbarianCombatantId).firstAttackMade) reason = BRUTAL_STRIKE_ERROR;
  else if (hasActiveEffect(combatState, barbarianCombatantId, 'brutal-strike-pending')) reason = 'Brutal Strike is already ready.';
  else if (brutalStrikeUsedThisTurn(combatState, barbarianCombatantId)) reason = 'Brutal Strike was already used this turn.';
  return { allowed: !reason, reason };
};

// Brutal Strike applies to one attack roll on each of your turns: the roll it was used on, hit or miss, spends it.
export const BRUTAL_STRIKE_USED = 'brutal-strike-used';
export const brutalStrikeUsedThisTurn = (combatState, combatantId) => (combatState?.activeEffects || []).some((effect) =>
  effect.definitionId === BRUTAL_STRIKE_USED && effect.sourceCombatantId === combatantId && effect.turnSequence === combatState?.turnSequence);
const brutalStrikeUsedEffect = (combatantId, turnSequence) => ({
  id: `${BRUTAL_STRIKE_USED}:${combatantId}`, definitionId: BRUTAL_STRIKE_USED, name: 'Brutal Strike used',
  sourceCombatantId: combatantId, targetCombatantId: combatantId, turnSequence, userVisible: false,
  expiration: { type: 'sourceTurn', combatantId, boundary: 'end', remainingOccurrences: 1, expireOnCurrentTurn: true },
  stackKey: `${BRUTAL_STRIKE_USED}:${combatantId}`, stackPolicy: 'replace',
});

/** Brutal Strike's extra damage dice: 1d10, or 2d10 from Barbarian 17 (Improved Brutal Strike). */
export const brutalStrikeDiceCount = (character) => (getBarbarianLevel(character) >= 17 ? 2 : 1);

export const createBrutalStrikePendingEffect = (combatantId, attackId) => ({
  id: `brutal-strike-pending:${combatantId}`,
  definitionId: 'brutal-strike-pending',
  name: 'Brutal Strike',
  sourceCombatantId: combatantId,
  targetCombatantId: combatantId,
  ...(attackId ? { attackId } : {}),
  expiration: { type: 'sourceTurn', combatantId, boundary: 'end', remainingOccurrences: 1, expireOnCurrentTurn: true },
  stackKey: `brutal-strike-pending:${combatantId}`,
  stackPolicy: 'ignore',
});

export const activateBrutalStrike = (input) => {
  const eligibility = canActivateBrutalStrike(input);
  if (!eligibility.allowed) throw new Error(eligibility.reason);
  return applyActiveEffect(input.combatState, createBrutalStrikePendingEffect(input.combatantId, input.attack?.id));
};

export const consumeBrutalStrikeOnAttackResolution = (combatState, combatantId) => {
  const pending = getBrutalStrikePendingEffect(combatState, combatantId);
  return pending ? removeActiveEffect(combatState, pending.id) : combatState;
};

export const createBrutalStrikeChoice = ({ resolutionId, sourceCombatantId, targetCombatantId, attackId, attackName, damageType, brutalStrikeDamage }) => ({
  id: `brutal-strike-choice:${resolutionId}`,
  definitionId: 'brutal-strike-choice-pending',
  name: 'Brutal Strike choice pending',
  resolutionId, sourceCombatantId, targetCombatantId, attackId, attackName, damageType,
  brutalStrikeDamage: Number(brutalStrikeDamage) || 0,
  expiration: { type: 'combatEnd' },
  stackKey: `brutal-strike-choice:${resolutionId}`,
  stackPolicy: 'ignore',
});

export const resolveBrutalStrikeAttack = (combatState, resolution) => {
  const consumed = applyActiveEffect(consumeBrutalStrikeOnAttackResolution(combatState, resolution.sourceCombatantId),
    brutalStrikeUsedEffect(resolution.sourceCombatantId, combatState?.turnSequence));
  if (resolution.outcome === 'miss') return consumed;
  return applyActiveEffect(consumed, createBrutalStrikeChoice(resolution));
};

export const getBrutalStrikeChoice = (combatState, sourceCombatantId) =>
  (combatState?.activeEffects || []).find((effect) => effect.definitionId === 'brutal-strike-choice-pending' && effect.sourceCombatantId === sourceCombatantId);

// Brutal Strike effects by level: Forceful Blow and Hamstring Blow (9), Staggering Blow and Sundering Blow (13). From
// Barbarian 17 a Brutal Strike applies two different effects.
export const brutalStrikeEffectOptions = (character) => (getBarbarianLevel(character) >= 13
  ? ['forceful', 'hamstring', 'staggering', 'sundering'] : ['forceful', 'hamstring']);
export const brutalStrikeEffectCount = (character) => (getBarbarianLevel(character) >= 17 ? 2 : 1);

/**
 * Applies the chosen effects this client writes (Hamstring, Staggering, Sundering; Forceful Blow is the server's
 * movement command). `pending` is the Brutal Strike hit; it is removed if it is still in the combat state.
 */
export const applyBrutalStrikeEffects = (combatState, pending, choices = []) => {
  let next = (combatState?.activeEffects || []).some((effect) => effect.id === pending.id) ? removeActiveEffect(combatState, pending.id) : combatState;
  for (const choice of choices) {
    if (choice === 'hamstring') next = applyHamstringBlow(next, pending);
    if (choice === 'staggering') next = applyActiveEffect(next, createStaggeringBlowEffect(pending));
    if (choice === 'sundering') next = applyActiveEffect(next, createSunderingBlowEffect(pending));
  }
  return { ...next, eventLog: [...(next.eventLog || []), ...choices.filter((choice) => choice !== 'forceful').map((choice) => ({
    type: 'brutalStrikeChoiceResolved', resolutionId: pending.resolutionId, choice,
    sourceCombatantId: pending.sourceCombatantId, targetCombatantId: pending.targetCombatantId,
  }))] };
};

export const applyBrutalStrikeChoice = (combatState, { resolutionId, choice, choices }) => {
  const pending = (combatState?.activeEffects || []).find((effect) => effect.definitionId === 'brutal-strike-choice-pending' && effect.resolutionId === resolutionId);
  if (!pending) return combatState;
  return applyBrutalStrikeEffects(combatState, pending, choices || [choice]);
};

const untilSourceTurnStarts = (sourceCombatantId) => ({ type: 'sourceTurn', combatantId: sourceCombatantId, boundary: 'start', remainingOccurrences: 1 });
export const STAGGERING_BLOW = 'barbarian:staggering-blow';
export const SUNDERING_BLOW = 'barbarian:sundering-blow';
export const createStaggeringBlowEffect = ({ sourceCombatantId, targetCombatantId }) => ({
  id: `staggering-blow:${targetCombatantId}:${Date.now()}`, definitionId: STAGGERING_BLOW, name: 'Staggering Blow', icon: '💫', userVisible: true,
  sourceCombatantId, targetCombatantId, expiration: untilSourceTurnStarts(sourceCombatantId),
  description: "Disadvantage on saving throws, and can't make Opportunity Attacks, until the start of the Barbarian's next turn.",
  stackKey: `staggering-blow:${targetCombatantId}`, stackPolicy: 'replace',
});
export const createSunderingBlowEffect = ({ sourceCombatantId, targetCombatantId }) => ({
  id: `sundering-blow:${targetCombatantId}:${Date.now()}`, definitionId: SUNDERING_BLOW, name: 'Sundering Blow', icon: '🪓', userVisible: true,
  sourceCombatantId, targetCombatantId, expiration: untilSourceTurnStarts(sourceCombatantId),
  description: "The next attack roll against this creature by anyone other than the Barbarian gets +5, before the start of the Barbarian's next turn.",
  stackKey: `sundering-blow:${targetCombatantId}`, stackPolicy: 'replace',
});

export const createHamstringBlowEffect = ({ sourceCombatantId, targetCombatantId }) => ({
  id: `hamstring-blow:${targetCombatantId}:${Date.now()}`,
  definitionId: 'hamstring-blow',
  name: 'Hamstring Blow',
  icon: '🦵',
  userVisible: true,
  sourceCombatantId,
  targetCombatantId,
  modifiers: [{ type: 'speed', operation: 'add', value: -15 }],
  expiration: { type: 'sourceTurn', combatantId: sourceCombatantId, boundary: 'start', remainingOccurrences: 1 },
  stackKey: `hamstring-blow:${targetCombatantId}`,
  stackPolicy: 'replace',
});

export const applyHamstringBlow = (combatState, input) =>
  applyActiveEffect(combatState, createHamstringBlowEffect(input));


export const getFrenzyState = (character) => ({
  usedThisTurn: Boolean(character?.classState?.barbarian?.frenzy?.usedThisTurn),
});

export const markFrenzyUsed = (character) => ({
  ...character,
  classState: {
    ...(character?.classState || {}),
    barbarian: {
      ...(character?.classState?.barbarian || {}),
      frenzy: { usedThisTurn: true },
    },
  },
});

export const resetFrenzyTurn = (character) => {
  if (!getFrenzyState(character).usedThisTurn) return character;
  return {
    ...character,
    classState: {
      ...(character?.classState || {}),
      barbarian: {
        ...(character?.classState?.barbarian || {}),
        frenzy: { usedThisTurn: false },
      },
    },
  };
};

export const getFrenzyDamageDice = (character, attack = {}) => {
  if (getBarbarianLevel(character) < 3 || !isPathOfTheBerserker(character)) return null;
  if (!isRageActive(character) || !getRecklessAttackState(character).active) return null;
  if (getFrenzyState(character).usedThisTurn) return null;
  if (attack.isOwnTurn === false) return null;
  if (attack.hit === false || attack.dealsDamage === false) return null;
  if (getRageDamageBonus(character, attack) <= 0) return null;
  return { label: "Frenzy", count: getRageDamageBonus(character, attack), sides: 6 };
};

